import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import CustomerLayout from "@/components/CustomerLayout";
import ChatThread from "@/components/ChatThread";
import { EmptyState, Spinner } from "@/components/States";
import { LifeBuoy, Plus, MessageSquare, User, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["order_issue", "missing_item", "wrong_item", "payment_issue", "delivery_issue", "refund_request", "general"];
const STATUS_COLOR = { open: "bg-amber-soft text-amber-warm", in_progress: "bg-blue-50 text-blue-600", resolved: "bg-brand-soft text-brand", closed: "bg-slate-100 text-slate-500" };

export default function Support() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [active, setActive] = useState(params.get("ticket") || null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "general", message: "" });
  const { data: tickets, isLoading } = useQuery({ queryKey: ["tickets"], queryFn: () => api.get("/support/tickets").then((r) => r.data), refetchInterval: 6000 });

  const submit = async () => {
    if (!form.subject || !form.message) return toast.error("Fill subject and message");
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success("Chat started — an agent will reply shortly");
      setShow(false); setForm({ subject: "", category: "general", message: "" });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      setActive(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <CustomerLayout>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-head text-2xl font-extrabold text-slate-900 sm:text-3xl">Support Chat</h1>
        <button data-testid="new-chat-btn" onClick={() => { setShow(!show); setActive(null); }} className="inline-flex items-center gap-1.5 rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> New chat</button>
      </div>

      {show && (
        <div className="mb-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What's this about?" data-testid="chat-subject" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Describe your issue…" rows={3} data-testid="chat-first-message" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <button onClick={submit} data-testid="start-chat-submit" className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white">Start chat</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* conversation list (hidden on mobile when a chat is open) */}
        <div className={`lg:col-span-1 ${active ? "hidden lg:block" : "block"}`}>
          <div className="space-y-2">
            {isLoading ? <Spinner /> : tickets?.length ? tickets.map((t) => (
              <button key={t.id} data-testid="ticket-item" onClick={() => setActive(t.id)}
                className={`w-full rounded-2xl border p-3 text-left transition-all ${active === t.id ? "border-brand bg-brand-soft/50" : "border-slate-200 bg-white hover:border-brand/40"}`}>
                <div className="flex items-center justify-between">
                  <p className="truncate font-semibold text-slate-800">{t.subject}</p>
                  <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${STATUS_COLOR[t.status]}`}>{t.status.replace(/_/g, " ")}</span>
                </div>
                <p className="flex items-center gap-1 text-xs text-slate-500">{t.assigned_agent_name ? <><User className="h-3 w-3" /> {t.assigned_agent_name}</> : "Awaiting agent"}</p>
              </button>
            )) : !show && <EmptyState icon={LifeBuoy} title="No chats yet" subtitle="Start a new chat to talk to our support team." />}
          </div>
        </div>

        {/* chat panel */}
        <div className={`lg:col-span-2 ${active ? "block" : "hidden lg:block"}`}>
          <div className="h-[68vh] overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {active ? (
              <div className="flex h-full flex-col">
                <button onClick={() => setActive(null)} className="flex items-center gap-1 border-b border-slate-100 p-2 text-xs font-semibold text-slate-500 lg:hidden"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
                <div className="flex-1 overflow-hidden"><ChatThread ticketId={active} mode="customer" /></div>
              </div>
            ) : <div className="flex h-full flex-col items-center justify-center text-center text-slate-400"><MessageSquare className="mb-3 h-10 w-10" /><p className="text-sm">Select or start a chat</p></div>}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
